import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  Pagination,
  Form,
  InputGroup,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { listarHistoricoPadrao, excluirHistoricoPadrao } from '~/Services/Service';
import { extrairMensagemErro } from '~/Utils/Helpers';
import ModalIncluir from './components/ModalIncluir';
import ModalEditar from './components/ModalEditar';

const PAGE_SIZE = 20;

const HistoricoPadrao = () => {
  const [dados, setDados] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [sortField, setSortField] = useState('codigo');
  const [sortDir, setSortDir] = useState('ASC');
  const [loading, setLoading] = useState(false);

  const [filtroCodigo, setFiltroCodigo] = useState('');
  const [filtroDescricao, setFiltroDescricao] = useState('');

  const [modalIncluirAberto, setModalIncluirAberto] = useState(false);
  const [modalEditarAberto, setModalEditarAberto] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });

  const carregarDados = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listarHistoricoPadrao({ page: 1, pageSize: 5000 });
      setDados((resp && resp.itens) || []);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Reseta paginação quando filtros mudam.
  useEffect(() => {
    setPagina(1);
  }, [filtroCodigo, filtroDescricao]);

  const dadosFiltrados = useMemo(() => {
    const codigoTrim = filtroCodigo.trim();
    const descricaoTrim = filtroDescricao.trim().toLowerCase();
    return dados.filter((item) => {
      if (codigoTrim) {
        const codStr = String(item.codigo_historico || '');
        if (codStr.indexOf(codigoTrim) < 0) return false;
      }
      if (descricaoTrim) {
        const desc = (item.descricao_historico || '').toLowerCase();
        if (desc.indexOf(descricaoTrim) < 0) return false;
      }
      return true;
    });
  }, [dados, filtroCodigo, filtroDescricao]);

  const dadosOrdenados = useMemo(() => {
    const dir = sortDir === 'ASC' ? 1 : -1;
    return dadosFiltrados.slice().sort((a, b) => {
      if (sortField === 'codigo') {
        return dir * ((a.codigo_historico || 0) - (b.codigo_historico || 0));
      }
      const va = (a.descricao_historico || '').toLowerCase();
      const vb = (b.descricao_historico || '').toLowerCase();
      return dir * va.localeCompare(vb);
    });
  }, [dadosFiltrados, sortField, sortDir]);

  const total = dadosOrdenados.length;
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const dadosPagina = useMemo(() => {
    const inicio = (pagina - 1) * PAGE_SIZE;
    return dadosOrdenados.slice(inicio, inicio + PAGE_SIZE);
  }, [dadosOrdenados, pagina]);

  // Clampa paginação se ficou fora do range (ex: após exclusão do último item).
  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  const handleSort = (campo) => {
    if (campo === sortField) {
      setSortDir((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortField(campo);
      setSortDir('ASC');
    }
    setPagina(1);
  };

  const iconeSort = (campo) => {
    if (campo !== sortField) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return sortDir === 'ASC'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const handleNovo = () => {
    setModalIncluirAberto(true);
  };

  const handleEditar = (item) => {
    setItemSelecionado(item);
    setModalEditarAberto(true);
  };

  const handleExcluir = (item) => {
    setConfirmacao({ show: true, item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    if (!confirmado) {
      setConfirmacao({ show: false, item: null });
      return;
    }
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    try {
      await excluirHistoricoPadrao({ codigo_historico: item.codigo_historico });
      toast.success('Histórico excluído com sucesso.');
      carregarDados();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  const handleSalvarInclusao = () => {
    setModalIncluirAberto(false);
    carregarDados();
  };

  const handleSalvarEdicao = () => {
    setModalEditarAberto(false);
    setItemSelecionado(null);
    carregarDados();
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1 && total === 0) return null;

    const maxBotoes = 5;
    const itens = [];
    let inicio = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
    if (fim - inicio < maxBotoes - 1) {
      inicio = Math.max(1, fim - maxBotoes + 1);
    }

    itens.push(
      <Pagination.First key="first" onClick={() => { setPagina(1); }} disabled={pagina === 1} />
    );
    itens.push(
      <Pagination.Prev key="prev" onClick={() => { setPagina((p) => Math.max(1, p - 1)); }} disabled={pagina === 1} />
    );
    if (inicio > 1) itens.push(<Pagination.Ellipsis key="el-s" disabled />);
    for (let n = inicio; n <= fim; n++) {
      const num = n;
      itens.push(
        <Pagination.Item key={num} active={num === pagina} onClick={() => { setPagina(num); }}>
          {num}
        </Pagination.Item>
      );
    }
    if (fim < totalPaginas) itens.push(<Pagination.Ellipsis key="el-e" disabled />);
    itens.push(
      <Pagination.Next key="next" onClick={() => { setPagina((p) => Math.min(totalPaginas, p + 1)); }} disabled={pagina === totalPaginas} />
    );
    itens.push(
      <Pagination.Last key="last" onClick={() => { setPagina(totalPaginas); }} disabled={pagina === totalPaginas} />
    );

    const inicio_ = total > 0 ? (pagina - 1) * PAGE_SIZE + 1 : 0;
    const fim_ = Math.min(pagina * PAGE_SIZE, total);

    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2 flex-wrap">
        <small className="text-muted">
          Mostrando {inicio_} até {fim_} de {total} registros
        </small>
        <Pagination size="sm" className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  const itemExclusao = confirmacao.item;

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Histórico Padrão" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-center">
            <Col md={3}>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  type="number"
                  placeholder="Código"
                  value={filtroCodigo}
                  onChange={(e) => { setFiltroCodigo(e.target.value); }}
                />
              </InputGroup>
            </Col>
            <Col md={5}>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Filtrar por descrição..."
                  value={filtroDescricao}
                  onChange={(e) => { setFiltroDescricao(e.target.value); }}
                />
              </InputGroup>
            </Col>
            <Col className="text-right">
              <Button size="sm" variant="primary" onClick={handleNovo}>
                <FaPlus className="mr-1" /> Novo histórico
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Histórico Padrão
            {total > 0 && (
              <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                ({total} {total === 1 ? 'registro' : 'registros'})
              </span>
            )}
          </strong>
        </Card.Header>

        <Card.Body className="p-0">
          {loading ? (
            <Loading />
          ) : total === 0 ? (
            <p className="text-muted text-center my-4">
              {dados.length === 0
                ? 'Nenhum histórico padrão cadastrado.'
                : 'Nenhum registro encontrado para os filtros aplicados.'}
            </p>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={{ width: '100px', ...thStyle }}
                        onClick={() => { handleSort('codigo'); }}
                      >
                        Código{iconeSort('codigo')}
                      </th>
                      <th
                        style={thStyle}
                        onClick={() => { handleSort('descricao'); }}
                      >
                        Descrição{iconeSort('descricao')}
                      </th>
                      <th style={{ width: '70px', textAlign: 'center' }}>Editar</th>
                      <th style={{ width: '70px', textAlign: 'center' }}>Excluir</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dadosPagina.map((item) => (
                      <tr key={`${item.codigo_historico}`}>
                        <td>{item.codigo_historico}</td>
                        <td>{item.descricao_historico}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="sm"
                            variant="outline-warning"
                            title="Editar"
                            onClick={() => { handleEditar(item); }}
                          >
                            <FaEdit />
                          </Button>
                        </td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            title="Excluir"
                            onClick={() => { handleExcluir(item); }}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {renderPaginacao()}
            </>
          )}
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={confirmacao.show}
        title="Confirmar Exclusão"
        message={
          itemExclusao
            ? `Deseja realmente excluir o histórico "${itemExclusao.descricao_historico}"?`
            : ''
        }
        onClose={handleConfirmarExclusao}
      />

      <ModalIncluir
        show={modalIncluirAberto}
        onHide={() => { setModalIncluirAberto(false); }}
        onSalvar={handleSalvarInclusao}
      />

      {modalEditarAberto && itemSelecionado && (
        <ModalEditar
          show={modalEditarAberto}
          onHide={() => {
            setModalEditarAberto(false);
            setItemSelecionado(null);
          }}
          item={itemSelecionado}
          onSalvar={handleSalvarEdicao}
        />
      )}
    </Container>
  );
};

export default HistoricoPadrao;
